/**
 * DOM layout for the POP template. Owns the hero + collection + manifesto + stories.
 * Returns { grid, cleanup } so template can attach scroll + click listeners.
 */
import { brand, products } from '../../products.js';
import { el, isDarkHex } from '../../core/utils.js';

function buildCard(p, i) {
  const dark = isDarkHex(p.colorA);
  const textColor = dark ? '#fff8ec' : '#0a0a0a';

  const variantChips = (p.variants && p.variants.length > 1)
    ? el('div', { className: 'variants' },
        ...p.variants.map((v, vi) =>
          el('button', {
            className: 'variant-chip' + (vi === 0 ? ' is-active' : ''),
            type: 'button',
            'aria-label': v.name || v.sku,
            title: v.name || v.sku,
            dataset: { variant: String(vi) },
            style: { background: v.colorA, borderColor: textColor },
          }),
        ),
      )
    : null;

  const buyBtn = p.stripeLink
    ? el('a', { className: 'action-btn buy-btn', href: p.stripeLink, target: '_blank', rel: 'noopener', dataset: { buy: 'true' } }, 'Buy')
    : null;

  const arBtn = p.model
    ? el('button', { className: 'action-btn ar-btn', type: 'button', dataset: { ar: 'true', model: p.model }, 'aria-label': 'View in AR' }, 'AR')
    : null;

  const customizeBtn = el('button', {
    className: 'action-btn customize-btn', type: 'button',
    dataset: { customize: 'true' }, 'aria-label': 'Customize colours', title: 'Customize',
  }, '✎');

  const customizeDrawer = el('div', { className: 'customize-drawer', hidden: true },
    el('label', {}, el('span', {}, 'Body'), el('input', { type: 'color', value: p.colorA, dataset: { slot: 'colorA' } })),
    el('label', {}, el('span', {}, 'Detail'), el('input', { type: 'color', value: p.colorB, dataset: { slot: 'colorB' } })),
    el('label', {}, el('span', {}, 'Accent'), el('input', { type: 'color', value: p.accent, dataset: { slot: 'accent' } })),
  );

  const actionRow = el('div', { className: 'actions' }, customizeBtn, arBtn, buyBtn);

  // Photoreal product cutout (Blender Cycles render) floating on the card.
  const photo = p.image
    ? el('div', { className: 'product-photo' },
        el('img', { src: p.image, alt: p.name, loading: 'lazy', draggable: 'false' }))
    : null;

  return el('li', {
    className: 'product-card' + (p.image ? ' has-photo' : ''),
    dataset: { index: String(i), mood: p.mood, productId: p.id },
    style: { background: p.colorA, color: textColor },
  },
    el('span', { className: 'tag', style: { color: p.accent } }, p.tag),
    photo,
    el('div', { className: 'product-body' },
      el('h3', {}, p.name),
      el('div', { className: 'price', dataset: { price: 'true' } }, p.price),
      variantChips,
      customizeDrawer,
    ),
    actionRow,
  );
}

export function popLayout(host, { embedded = false } = {}) {
  host.classList.add('tpl-pop');
  if (embedded) host.classList.add('tpl-embedded');

  const grid = el('ol', { className: 'watches', id: 'product-grid' });
  for (let i = 0; i < products.length; i++) grid.appendChild(buildCard(products[i], i));

  // Heading: stores show their drop name; agency demo shows a neutral label.
  const collectionTitle = embedded
    ? (brand.drop?.label || 'The Collection')
    : 'The Family';
  const collection = el('section', { className: 'collection', id: 'collection', dataset: { scene: 'collection' } },
    el('header', { className: 'block-head' },
      el('h2', {}, collectionTitle),
      el('p', {}, brand.tagline),
    ),
    grid,
  );

  // EMBEDDED mode: render ONLY the collection (the 3D-driven carousel).
  // Agency wrapper provides hero, manifesto, footer.
  if (embedded) {
    const main = el('main', { className: 'tpl-pop-main tpl-pop-main--embedded' }, collection);
    host.appendChild(main);
    return {
      grid,
      cleanup() {
        host.classList.remove('tpl-pop', 'tpl-embedded');
        main.remove();
      },
    };
  }

  // STANDALONE mode: full marketing site.
  const nav = el('header', { className: 'nav' },
    el('a', { className: 'logo', href: '#' }, brand.name),
    el('nav', {},
      el('a', { href: '#collection' }, 'Collection'),
      el('a', { href: '#stories' }, 'Stories'),
      el('a', { href: '#shop' }, 'Shop'),
    ),
  );

  const headline = el('h1', {});
  const headlineParts = brand.hero.headline.split(/<em>(.+?)<\/em>/);
  headlineParts.forEach((part, i) => {
    const lines = part.split('\n');
    lines.forEach((line, j) => {
      if (j > 0) headline.appendChild(document.createElement('br'));
      if (i % 2 === 1) headline.appendChild(el('em', {}, line));
      else headline.appendChild(document.createTextNode(line));
    });
  });

  const hero = el('section', { className: 'hero', dataset: { scene: 'hero' } },
    el('div', { className: 'hero__copy' },
      el('p', { className: 'eyebrow' }, brand.hero.eyebrow),
      headline,
      el('p', { className: 'lede' }, brand.hero.lede),
      el('a', { className: 'cta', href: '#collection' }, brand.hero.cta),
    ),
    el('div', { className: 'hero__hint', 'aria-hidden': 'true' }, 'scroll ↓'),
  );

  const manifestoTitle = el('h2', {});
  brand.manifesto.title.split('\n').forEach((line, i, arr) => {
    manifestoTitle.appendChild(document.createTextNode(line));
    if (i < arr.length - 1) manifestoTitle.appendChild(document.createElement('br'));
  });

  const manifesto = el('section', { className: 'manifesto', dataset: { scene: 'manifesto' } },
    manifestoTitle,
    el('p', {}, brand.manifesto.body),
  );

  const stories = el('section', { className: 'stories', id: 'stories', dataset: { scene: 'stories' } },
    el('header', { className: 'block-head' }, el('h2', {}, 'Stories')),
    el('div', { className: 'story-row' },
      el('article', {}, el('h3', {}, 'Behind the Drop'), el('p', {}, 'How the SS·26 palette came to life.')),
      el('article', {}, el('h3', {}, 'The Manifesto'), el('p', {}, 'Loud, lighter, on time.')),
      el('article', {}, el('h3', {}, 'The Process'), el('p', {}, 'From sketch to shelf in 30 days.')),
    ),
  );

  const footer = el('footer', { id: 'shop' },
    el('p', {}, '© ', el('span', {}, brand.name), ' — Demo build.'),
    el('p', {}, 'Scroll the future.'),
  );

  const main = el('main', { className: 'tpl-pop-main' }, hero, collection, manifesto, stories, footer);
  host.appendChild(nav);
  host.appendChild(main);

  return {
    grid,
    cleanup() {
      host.classList.remove('tpl-pop');
      nav.remove();
      main.remove();
    },
  };
}
